import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Send, ChevronDown, Settings } from 'lucide-react';
import * as supportApi from '../services/supportApi';
import { listEnterprises, listTeams } from '../services/organizationApi';
import type { Conversation, Message } from '../services/supportApi';

const CATEGORY_COLORS: Record<string, string> = {
  easycharts: 'bg-purple-100 text-purple-800',
  easykaizen: 'bg-red-100 text-red-800',
  easyclient: 'bg-green-100 text-green-800',
  easyproduct: 'bg-indigo-100 text-indigo-800',
  easyprod: 'bg-orange-100 text-orange-800',
  easyplan: 'bg-teal-100 text-teal-800',
  easyvision: 'bg-blue-100 text-blue-800',
  mobile: 'bg-amber-100 text-amber-800',
  other: 'bg-gray-100 text-gray-800',
};

const CATEGORY_LABELS: Record<string, string> = {
  easycharts: 'EasyCharts',
  easykaizen: 'EasyKaizen',
  easyclient: 'EasyClient',
  easyproduct: 'EasyProduct',
  easyprod: 'EasyProduction',
  easyplan: 'EasyPlan',
  easyvision: 'EasyVision',
  mobile: 'App Mobile',
  other: 'Autre',
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Ouvert', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'in_progress', label: 'En cours', color: 'bg-blue-100 text-blue-800' },
  { value: 'resolved', label: 'Résolu', color: 'bg-green-100 text-green-800' },
  { value: 'closed', label: 'Fermé', color: 'bg-gray-100 text-gray-800' },
];

export default function SupportPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [enterprises, setEnterprises] = useState<{ id: string; name: string }[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [configEnterprise, setConfigEnterprise] = useState('');
  const [configTeam, setConfigTeam] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversations();
    loadConfig();
  }, []);

  // Read-only chargement de la config (edition centralisee dans /settings)
  const loadConfig = async () => {
    try {
      const settings = await supportApi.getSettings();
      setConfigEnterprise(settings.target_enterprise_id || '');
      setConfigTeam(settings.target_team_id || '');
      const ents = await listEnterprises();
      setEnterprises(ents);
      if (settings.target_enterprise_id) {
        const t = await listTeams(settings.target_enterprise_id);
        setTeams(t);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages when a conversation is open
  useEffect(() => {
    if (!selectedConv) return;
    const interval = setInterval(() => {
      openConversation(selectedConv);
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedConv?.id]);

  // Poll for new conversations
  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      const { conversations } = await supportApi.listConversations();
      setConversations(conversations);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openConversation = async (conv: Conversation) => {
    try {
      const { conversation, messages } = await supportApi.getConversation(conv.id);
      setSelectedConv(conversation);
      setMessages(messages);
    } catch (error) {
      console.error('Failed to open conversation:', error);
    }
  };

  const handleSend = async () => {
    if (!messageInput.trim() || !selectedConv) return;
    setIsSending(true);
    try {
      const newMessage = await supportApi.sendMessage(selectedConv.id, messageInput.trim());
      setMessages((prev) => [...prev, newMessage]);
      setMessageInput('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleStatusChange = async (convId: number, status: string) => {
    try {
      const updated = await supportApi.updateStatus(convId, status);
      setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, status: updated.status } : c)));
      if (selectedConv?.id === convId) {
        setSelectedConv((prev) => prev ? { ...prev, status: updated.status } : null);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const filteredConversations = statusFilter === 'all'
    ? conversations
    : conversations.filter((c) => c.status === statusFilter);

  const getStatusStyle = (status: string) =>
    STATUS_OPTIONS.find((s) => s.value === status)?.color || 'bg-gray-100 text-gray-800';

  const getStatusLabel = (status: string) =>
    STATUS_OPTIONS.find((s) => s.value === status)?.label || status;

  const getLocaleFlag = (locale: string) => {
    const flags: Record<string, string> = {
      fr: '🇫🇷', en: '🇬🇧', de: '🇩🇪', es: '🇪🇸', it: '🇮🇹', nl: '🇳🇱', pt: '🇵🇹', 'pt-BR': '🇧🇷',
    };
    return flags[locale] || locale;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Read-only routing banner — edition centralisee dans /settings */}
      {!configEnterprise ? (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-yellow-800">
            Routage des problèmes non configuré : les conversations support et les retours du footer ne créeront pas de problèmes.
          </p>
          <Link to="/settings" className="text-sm text-yellow-700 font-medium hover:text-yellow-900 flex items-center gap-1">
            <Settings className="h-4 w-4" /> Configurer
          </Link>
        </div>
      ) : (
        <div className="bg-green-50 border-b border-green-200 px-4 py-1.5 flex items-center justify-between">
          <p className="text-xs text-green-700">
            Problèmes créés dans : {enterprises.find(e => e.id === configEnterprise)?.name ?? '—'} / {teams.find(t => t.id === configTeam)?.name ?? '—'}
          </p>
          <Link to="/settings" className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      <div className="flex-1 flex min-h-0">
      {/* Left: Conversation list */}
      <div className="w-96 border-r border-gray-200 flex flex-col bg-white">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Support
          </h2>
          <div className="mt-2 flex gap-1 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`text-xs px-2 py-1 rounded-full ${statusFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Tous ({conversations.length})
            </button>
            {STATUS_OPTIONS.map((s) => {
              const count = conversations.filter((c) => c.status === s.value).length;
              return (
                <button
                  key={s.value}
                  onClick={() => setStatusFilter(s.value)}
                  className={`text-xs px-2 py-1 rounded-full ${statusFilter === s.value ? 'bg-gray-900 text-white' : s.color}`}
                >
                  {s.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">Aucune conversation</p>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => openConversation(conv)}
                className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  selectedConv?.id === conv.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {getLocaleFlag(conv.user_locale)} {conv.subject_fr || conv.subject}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ml-2 ${getStatusStyle(conv.status)}`}>
                    {getStatusLabel(conv.status)}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  {conv.category && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[conv.category] || 'bg-gray-100 text-gray-800'}`}>
                      {CATEGORY_LABELS[conv.category] || conv.category}
                    </span>
                  )}
                  {conv.problem_id && (
                    <span className="text-xs text-gray-500">#{conv.problem_id}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {conv.first_name} {conv.last_name} · {conv.enterprise_name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(conv.updated_at).toLocaleString('fr-FR')}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: Chat */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {!selectedConv ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Sélectionnez une conversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {getLocaleFlag(selectedConv.user_locale)} {selectedConv.subject_fr || selectedConv.subject}
                </h3>
                {selectedConv.subject_fr && selectedConv.subject !== selectedConv.subject_fr && (
                  <p className="text-xs text-gray-500 mt-0.5">Original: {selectedConv.subject}</p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-xs text-gray-500">
                    {selectedConv.first_name} {selectedConv.last_name} · {selectedConv.user_email}
                  </p>
                  <span className="text-xs text-gray-400">|</span>
                  <p className="text-xs text-gray-500">
                    {selectedConv.enterprise_name}
                  </p>
                  {selectedConv.category && (
                    <>
                      <span className="text-xs text-gray-400">|</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[selectedConv.category] || 'bg-gray-100 text-gray-800'}`}>
                        {CATEGORY_LABELS[selectedConv.category] || selectedConv.category}
                      </span>
                    </>
                  )}
                  {selectedConv.problem_id && (
                    <>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">Ticket #{selectedConv.problem_id}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="relative">
                <select
                  value={selectedConv.status}
                  onChange={(e) => handleStatusChange(selectedConv.id, e.target.value)}
                  className={`text-xs px-3 py-1.5 rounded-full appearance-none cursor-pointer pr-7 ${getStatusStyle(selectedConv.status)}`}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <ChevronDown className="h-3 w-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'support' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-xl px-4 py-3 ${
                    msg.sender_type === 'support'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}>
                    <p className={`text-xs font-semibold mb-1 ${msg.sender_type === 'support' ? 'text-blue-200' : 'text-gray-500'}`}>
                      {msg.sender_type === 'user'
                        ? `${selectedConv.first_name ?? ''} ${selectedConv.last_name ?? ''}`.trim()
                        : 'Support'}
                    </p>
                    {msg.images?.length > 0 && (
                      <div className="flex gap-2 flex-wrap mb-2">
                        {msg.images.map((img) => (
                          <img key={img.id} src={img.data} alt={img.alt || ''} className="max-w-full rounded-lg max-h-48 cursor-pointer" onClick={() => setZoomedImage(img.data)} />
                        ))}
                      </div>
                    )}
                    {msg.sender_type === 'user' && (
                      <p className="text-sm font-medium mb-1">
                        {msg.translated_text || msg.original_text}
                      </p>
                    )}
                    {msg.sender_type === 'user' && msg.translated_text && (
                      <p className="text-xs text-gray-500 border-t border-gray-200 pt-1 mt-1">
                        Original ({msg.original_locale}): {msg.original_text}
                      </p>
                    )}
                    {msg.sender_type === 'support' && (
                      <>
                        <p className="text-sm">{msg.original_text}</p>
                        {msg.translated_text && (
                          <p className="text-xs opacity-70 border-t border-white/20 pt-1 mt-1">
                            → {selectedConv.user_locale}: {msg.translated_text}
                          </p>
                        )}
                      </>
                    )}
                    <p className={`text-xs mt-1 ${msg.sender_type === 'support' ? 'text-blue-200' : 'text-gray-400'}`}>
                      {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {selectedConv.status !== 'closed' && (
              <div className="bg-white border-t border-gray-200 p-4">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Répondre en français (traduction automatique)..."
                    disabled={isSending}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!messageInput.trim() || isSending}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {isSending ? 'Envoi...' : 'Envoyer'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Votre réponse sera automatiquement traduite en {selectedConv.user_locale.toUpperCase()} pour l'utilisateur
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>

      {/* Image zoom modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt=""
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
